import { ILoanQuote } from '@/app/models/loanQuote'
import { CookiesEnum } from '@/app/enums/Cookies'
import { deleteCookie, getCookie } from '@/app/utils/cookie-utils'
import { applyPromoCode, removePromoCode } from '@/app/endpoints/loanComparatorEndpoints'


export const RM_APPLICATION_FEE_PER_BORROWER = 80

// Fees are round euro amounts: "80 €" reads better than "80,00 €" next to the premiums
export function formatFeeAmount(amount: number): string {
    return `${amount.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`
}

// Hides the promo code UI until marketing launches it; the fee lines on offer cards stay visible
export function isLoanPromoCodeEnabled(): boolean {
    return process.env.NEXT_PUBLIC_LOAN_PROMO_CODE_ENABLED === '1'
}

export function activatedBorrowerCount(quote: ILoanQuote | null | undefined): number {
    const activated = quote?.borrowers?.filter((borrower) => borrower.activated !== false).length ?? 0
    return Math.max(1, activated)
}

export function rmFeeAmount(quote: ILoanQuote | null | undefined): number {
    return RM_APPLICATION_FEE_PER_BORROWER * activatedBorrowerCount(quote)
}

export function promoDiscountAmount(quote: ILoanQuote | null | undefined): number {
    return quote?.promo_code ? (quote.promo_discount_amount ?? 0) : 0
}

export function rmFeeAmountAfterPromo(quote: ILoanQuote | null | undefined): number {
    return Math.max(0, rmFeeAmount(quote) - promoDiscountAmount(quote))
}

export function promoShortLabel(quote: ILoanQuote | null | undefined): string | null {
    const promo = quote?.promo_code
    if (!promo) return null
    if (promo.discount_type === 'percent') return `–${promo.discount_value} %`
    return `–${formatFeeAmount(promoDiscountAmount(quote))}`
}

export function describePromoDiscount(quote: ILoanQuote | null | undefined, withAmounts = true): string | null {
    const promo = quote?.promo_code
    if (!promo) return null
    const fees = rmFeeAmount(quote)
    const after = rmFeeAmountAfterPromo(quote)
    const amounts = withAmounts ? ` (${formatFeeAmount(fees)} → ${formatFeeAmount(after)})` : ''
    if (after === 0) {
        return withAmounts ? `Vos frais de dossier (${formatFeeAmount(fees)}) sont offerts.` : 'Vos frais de dossier sont offerts'
    }
    return `${promoShortLabel(quote)} sur vos frais de dossier${amounts}`
}

export function readPromoCodeCookie(): string | null {
    const value = getCookie(CookiesEnum.LOAN_PROMO_CODE)?.trim().toUpperCase()
    return value ? value : null
}

export function clearPromoCodeCookie(): void {
    deleteCookie(CookiesEnum.LOAN_PROMO_CODE)
    deleteCookie(CookiesEnum.LOAN_PROMO_CODE, process.env.NEXT_PUBLIC_COOKIE_DOMAIN || '.reassurez-moi.fr')
}

// A code applied before the email was known may already have been used by this person: drop it now and return it, so the
// results can say why, instead of the pricing dropping it silently
export async function bindPromoCodeToEmail(quote: ILoanQuote, typedCode: string, email: string): Promise<string | null> {
    const code = quote.promo_code?.code ?? typedCode
    if (!code) return null
    const result = await applyPromoCode(quote.token, code, email).catch(() => null)
    if (result?.reason !== 'user_limit') return null
    if (quote.promo_code) await removePromoCode(quote.token).catch(() => null)
    return code
}
